import React from "react";
import { Link } from "react-router-dom";
import heroImage from "../assets/ema-hero.jpeg";
import useScrollReveal from "../hooks/useScrollReveal";

export default function Home() {
  useScrollReveal();
  return (
    <main className="ema">
      {/* HERO FULL SCREEN */}
      <section
        className="ema-hero-full"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="ema-hero-overlay">
          <div className="ema-hero-content">
            <p className="ema-hero-eyebrow">
              Institution de formation judiciaire
            </p>

            <h1 className="ema-hero-title">
              École de la Magistrature
            </h1>

            <p className="ema-hero-subtitle">
              Former les magistrats, greffiers et acteurs de la Justice à travers une
              formation moderne, rigoureuse et centrée sur l’excellence.
            </p>
          </div>
        </div>
      </section>

      {/* MOT DU DIRECTEUR */}
      <section className="ema-section">
        <div className="ema-container">
          <div className="ema-director">
            <div className="ema-director-photoWrap">
              <img
                src="/gouvernance/directeur.jpg"
                alt="Directeur de l’École de la Magistrature"
                className="ema-director-photo"
                onError={(e) =>
                  (e.currentTarget.src = "/gouvernance/placeholder.jpg")
                }
              />
            </div>

            <div className="ema-director-body">
              <p className="ema-kicker">Mot du Directeur</p>
              <h2 className="ema-h2" style={{ marginTop: 8 }}>
                Une formation judiciaire exigeante, au service de l’État de droit
              </h2>

              <p className="ema-paragraph" style={{ marginTop: 10 }}>
                Bienvenue sur la plateforme officielle de l’École de la
                Magistrature. Notre mission est de former des magistrats,
                greffiers et professionnels de la Justice avec rigueur, éthique
                et excellence, en combinant théorie, pratique, simulations
                d’audience et stages en juridiction.
              </p>

              <p className="ema-paragraph" style={{ marginTop: 10 }}>
                Nous vous invitons à découvrir nos programmes, nos activités et
                les opportunités offertes au sein de notre institution.
              </p>

              <p className="ema-muted" style={{ marginTop: 12 }}>
                <strong>Me. Kesner Michel Thermesi</strong> — Directeur général
                ai. de l’École de la Magistrature
              </p>

              <div className="ema-inline-actions" style={{ marginTop: 14 }}>
                <Link className="ema-btn ema-btn-secondary" to="/gouvernance">
                  Découvrir la gouvernance
                </Link>
                <Link className="ema-btn ema-btn-ghost" to="/formations">
                  Voir les formations
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONSEIL DE DIRECTION */}
      <section className="ema-section ema-reveal">
        <div className="ema-container">
          <div className="ema-section-head">
            <div>
              <p className="ema-kicker">Gouvernance</p>
              <h2 className="ema-h2">Conseil de Direction</h2>
              <p className="ema-lead">
                L’équipe dirigeante assure l’orientation stratégique, la qualité
                académique et la gestion administrative de l’École de la
                Magistrature.
              </p>
            </div>

            <Link className="ema-btn ema-btn-ghost" to="/gouvernance">
              Voir la gouvernance →
            </Link>
          </div>

          <div className="ema-grid-3">
            <PersonCard
              role="Directeur général ai."
              name="Kesner Michel Thermesi"
              title="Magistrat / Chef d’établissement"
              photo="/gouvernance/directeur.jpg"
              photoPosition="center 22%"
              to="/gouvernance"
            />
            <PersonCard
              role="Directrice des études"
              name="Maguy Florestal"
              title="Responsable académique & pédagogie"
              summary="Magistrate chargée de l’orientation académique, de la coordination pédagogique et du suivi des programmes de formation."
              photo="/gouvernance/directrice-etudes.jpeg"
              photoPosition="center 18%"
              to="/gouvernance/maguy-florestal"
            />
            <PersonCard
              role="Administratrice"
              name="Jeanne Béanne Casty"
              title="Administration & finances"
              photo="/gouvernance/administratrice.jpeg"
              photoPosition="center 20%"
              to="/gouvernance/jeanne-beanne-casty"
            />
          </div>
        </div>
      </section>

      {/* ACCÈS RAPIDES */}
      <section className="ema-section ema-reveal">
        <div className="ema-container">
          <h2 className="ema-h2">Accès rapides</h2>
          <p className="ema-lead">Les liens essentiels pour les candidats et le public.</p>

          <div className="ema-grid-4">
            <QuickCard
              title="Concours & conditions"
              text="Consultez les critères d’éligibilité, les pièces requises et les étapes du processus de sélection."
              to="/concours"
              cta="Voir les conditions"
            />
            <QuickCard
              title="Inscription en ligne"
              text="Créez votre compte et soumettez votre dossier en toute sécurité, avec un suivi en temps réel."
              to="/register"
              cta="Créer un compte"
            />
            <QuickCard
              title="Résultats & listes"
              text="Accédez aux listes officielles publiées par l’École (admissibilité, admission, convocations)."
              to="/announcements"
              cta="Voir les résultats"
            />
            <QuickCard
              title="Convocations & documents"
              text="Téléchargez vos convocations, formulaires et documents utiles (PDF)."
              to="/ressources"
              cta="Accéder aux documents"
            />
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="ema-section ema-section-alt ema-reveal">
        <div className="ema-container ema-grid-2">
          <div>
            <h2 className="ema-h2">Notre mission</h2>
            <p className="ema-paragraph">
              L’École de la Magistrature est une institution dédiée à la
              formation des professionnels de la Justice. Elle contribue à
              renforcer la compétence, l’éthique et l’efficacité du système
              judiciaire, en soutenant l’amélioration continue des pratiques et
              des standards.
            </p>
          </div>

          <div className="ema-panel">
            <h3 className="ema-h3">Nos missions</h3>
            <ul className="ema-list">
              <li>
                Assurer la <strong>formation initiale</strong> des futurs magistrats et
                greffiers
              </li>
              <li>
                Déployer une <strong>formation continue</strong> adaptée aux évolutions du
                droit et des pratiques
              </li>
              <li>
                Produire et diffuser des <strong>ressources pédagogiques</strong> et guides
                professionnels
              </li>
              <li>
                Développer la <strong>coopération</strong> avec les institutions nationales et
                partenaires internationaux
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* FORMATIONS */}
      <section className="ema-section ema-reveal">
        <div className="ema-container">
          <div className="ema-section-head">
            <div>
              <h2 className="ema-h2">Nos programmes de formation</h2>
              <p className="ema-lead">
                Des parcours structurés combinant théorie, pratique, méthodologie
                et déontologie.
              </p>
            </div>
            <Link className="ema-btn ema-btn-secondary" to="/formations">
              Voir toutes les formations
            </Link>
          </div>

          <div className="ema-grid-4">
            <ProgramCard
              title="Formation initiale (Magistrature)"
              text="Parcours complet : enseignements juridiques, cas pratiques, rédaction, éthique, simulations d’audience."
              to="/formations"
            />
            <ProgramCard
              title="Formation initiale (Greffe)"
              text="Procédures, organisation judiciaire, rédaction, rigueur administrative et compétences opérationnelles."
              to="/formations"
            />
            <ProgramCard
              title="Formation continue"
              text="Renforcement pour magistrats et greffiers : contentieux, management, technologies, déontologie."
              to="/formations"
            />
            <ProgramCard
              title="Formation probatoire"
              text="Renforcement pour magistrats et greffiers : contentieux, management, technologies, déontologie."
              to="/formations"
            />
            <ProgramCard
              title="Séminaires & ateliers"
              text="Ateliers pratiques : rédaction des décisions, techniques d’audience, méthodologie du jugement."
              to="/formations"
            />
          </div>
        </div>
      </section>

      {/* CONCOURS */}
      <section className="ema-section ema-section-alt ema-reveal">
        <div className="ema-container">
          <div className="ema-section-head">
            <div>
              <h2 className="ema-h2">Concours & admissions</h2>
              <p className="ema-lead">
                Un processus clair, sécurisé et transparent, conçu pour accompagner
                chaque candidat.
              </p>
            </div>
            <Link className="ema-btn ema-btn-primary" to="/register">
              Démarrer une candidature
            </Link>
          </div>

          <div className="ema-timeline">
            <TimelineStep
              n="1"
              title="Vérifier son éligibilité"
              text="Prenez connaissance des conditions, pièces et délais officiels."
              to="/concours"
              cta="Lire les conditions"
            />
            <TimelineStep
              n="2"
              title="Créer son compte candidat"
              text="Enregistrez vos informations et sécurisez votre accès."
              to="/register"
              cta="Créer un compte"
            />
            <TimelineStep
              n="3"
              title="Soumettre le dossier + pièces"
              text="Téléversez vos documents, complétez votre candidature et validez."
              to="/candidate-dashboard"
              cta="Accéder au tableau de bord"
            />
            <TimelineStep
              n="4"
              title="Suivre l’évolution du dossier"
              text="Consultez l’état d’avancement, les notifications, convocations et résultats."
              to="/login"
              cta="Se connecter"
            />
          </div>
        </div>
      </section>

      {/* ACTUALITÉS & COMMUNIQUÉS - Style éditorial */}
      <section className="ema-section ema-reveal" style={{ padding: '80px 0' }}>
        <div className="ema-container">
          <div className="ema-section-head">
            <div>
              <span className="ema-kicker">Actualités</span>
              <h2 className="ema-h2">Actualités & communiqués</h2>
              <p className="ema-lead">
                Suivez les annonces officielles, les activités académiques et les
                événements de l’École.
              </p>
            </div>
            <Link className="ema-btn ema-btn-secondary" to="/announcements">
              Voir toutes les publications
            </Link>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
            <Link to="/actualites/prestation-serment-8e-promotion" style={{
              textDecoration: 'none', borderRadius: '20px', overflow: 'hidden',
              background: '#fff', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)', transition: 'all 0.3s'
            }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(11,44,95,0.08)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)'; }}
            >
              <div style={{ height: '220px', overflow: 'hidden', position: 'relative' }}>
                <img src="/media/photos/cer1.jpeg" alt="Prestation de serment 8e promotion" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', top: '16px', left: '16px', display: 'flex', gap: '8px' }}>
                  <span style={{ background: '#DC2626', color: '#fff', padding: '4px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 700 }}>A la une</span>
                  <span style={{ background: '#0B2C5F', color: '#fff', padding: '4px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 700 }}>CSPJ</span>
                </div>
              </div>
              <div style={{ padding: '28px' }}>
                <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600, marginBottom: '8px' }}>23 juin 2026</div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0B2C5F', margin: '0 0 12px', lineHeight: 1.3 }}>8e promotion de l'EMA : 49 élèves magistrats, dont 13 femmes, prêteront  serment</h3>
                <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: 1.6, margin: '0 0 16px' }}>
                  Le CSPJ informe qu'une céremonie de prestation de serment des élèves magistrats de la 8e promotion se tiendra le 26 juin 2026.
                </p>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ color: '#0B2C5F', fontWeight: 600, fontSize: '14px' }}>Lire le communiqué→</span>
                  <span style={{ color: '#6B7280', fontSize: '12px' }}>11 photos</span>
                </div>
              </div>
            </Link>

            {/* Colonne laterale */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { date: '20 juillet 2026', category: 'Institutionnel', title: 'Commission technique pour la réforme pénale', color: '#DC2626' },
                { date: '26 juin 2026', category: 'Communiqué', title: 'Note de départ en stage pour les élèves magistrats', color: '#0B2C5F' },
                { date: '23 juin 2026', category: 'Vie de l\'École', title: 'Match des Élèves Magistrats en l\'honneur des Grenadiers', color: '#059669' }
              ].map((item, i) => (
                <Link key={i} to="/announcements" style={{
                  textDecoration: 'none', padding: '20px', background: '#fff',
                  border: '1px solid #e5e7eb', borderRadius: '16px',
                  transition: 'all 0.2s', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'
                }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = '#0B2C5F'; e.currentTarget.style.transform = 'translateX(4px)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.transform = 'translateX(0)'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color }} />
                    <span style={{ fontSize: '11px', fontWeight: 700, color: item.color, textTransform: 'uppercase' }}>{item.category}</span>
                    <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: 'auto' }}>{item.date}</span>
                  </div>
                  <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', margin: 0, lineHeight: 1.4 }}>{item.title}</h4>
                </Link>
              ))}
              <Link to="/announcements" style={{
                textDecoration: 'none', padding: '16px 20px',
                background: 'linear-gradient(135deg, #0B2C5F, #1e40af)',
                borderRadius: '16px', textAlign: 'center', color: '#fff',
                fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
              }}>
                Tous les communiqués
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </Link>
            </div>
            {/* FIN ACTUALITES GRID */}
          </div>
        </div>
      </section>

      {/* CHIFFRES CLÉS */}
      <section className="ema-section ema-section-alt ema-reveal">
        <div className="ema-container">
          <h2 className="ema-h2">L’École en chiffres</h2>
          <p className="ema-lead">Indicateurs institutionnels (à ajuster avec tes données réelles).</p>

          <div className="ema-stats">
            <Stat label="Promotions formées" value="8" />
            <Stat label="Apprenants accompagnés" value="363" />
            <Stat label="Formateurs mobilisés" value="30+" />
            <Stat label="Partenariats" value="4" />
          </div>
        </div>
      </section>

      {/* MAGISTRATS ISSUS DE L'EMA - Section Premium */}
      <section className="ema-section ema-section-premium ema-reveal">
        <div className="ema-container">
          <div className="ema-section-head">
            <div>
              <p className="ema-kicker">Excellence & Impact</p>
              <h2 className="ema-h2">Les magistrats issus de l'EMA</h2>
              <p className="ema-lead">
                Découvrez les parcours exceptionnels de nos diplômés qui servent la justice haïtienne
                dans les tribunaux à travers tout le pays.
              </p>
            </div>
            <Link className="ema-btn ema-btn-primary" to="/magistrats-ema">
              Découvrir →
            </Link>
          </div>

          <div className="ema-grid-3" style={{ marginTop: 20 }}>
            <div className="ema-card ema-card-highlight">
              <div className="ema-card-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-4z"/>
                  <path d="M12 8v8"/>
                  <path d="M8 12h8"/>
                </svg>
              </div>
              <h3 className="ema-h3">Juges de paix</h3>
              <p className="ema-paragraph">
                Justice de proximité dans les communes, résolution des conflits locaux et médiation.
              </p>
              <div className="ema-card-meta">
                <span className="ema-badge ema-badge-success">150+ en fonction</span>
              </div>
            </div>

            <div className="ema-card ema-card-highlight">
              <div className="ema-card-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18"/>
                  <path d="M5 21V7l8-4 8 4v14"/>
                  <path d="M9 10a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2H9V10z"/>
                  <path d="M12 21V12"/>
                </svg>
              </div>
              <h3 className="ema-h3">Tribunaux de première instance</h3>
              <p className="ema-paragraph">
                Juridictions de droit commun, instruction et jugement des affaires civiles et pénales.
              </p>
              <div className="ema-card-meta">
                <span className="ema-badge ema-badge-success">80+ en fonction</span>
              </div>
            </div>

            <div className="ema-card ema-card-highlight">
              <div className="ema-card-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18"/>
                  <path d="M5 21V7l8-4 8 4v14"/>
                  <path d="M9 10a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2H9V10z"/>
                  <path d="M12 21V12"/>
                  <path d="M12 12l-3 3"/>
                  <path d="M12 12l3 3"/>
                </svg>
              </div>
              <h3 className="ema-h3">Cours d'appel</h3>
              <p className="ema-paragraph">
                Second degré juridictionnel, contrôle des décisions et unification de la jurisprudence.
              </p>
              <div className="ema-card-meta">
                <span className="ema-badge ema-badge-success">45+ en fonction</span>
              </div>
            </div>
          </div>

          <div className="ema-testimonials" style={{ marginTop: 32 }}>
            <h3 className="ema-h3" style={{ marginBottom: 16 }}>Témoignages</h3>
            <div className="ema-grid-2">
              <div className="ema-panel">
                <p className="ema-quote">
                  "La formation à l'EMA m'a donné les outils nécessaires pour exercer ma fonction avec rigueur et intégrité.
                  Chaque jour, je m'efforce de rendre justice avec équité et professionnalisme."
                </p>
                <div className="ema-quote-author">
                  <strong>Juge Jean Dupont</strong>
                  <span className="ema-muted">Tribunal de première instance de Port-au-Prince</span>
                </div>
              </div>

              <div className="ema-panel">
                <p className="ema-quote">
                  "L'EMA m'a préparée aux défis de la magistrature. Les stages pratiques et les enseignements théoriques
                  m'ont permis de développer les compétences essentielles à mon rôle de juge."
                </p>
                <div className="ema-quote-author">
                  <strong>Juge Marie Pierre</strong>
                  <span className="ema-muted">Cour d'appel de Cap-Haïtien</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCÈS FICTIFS - Section Pédagogique */}
      <section className="ema-section ema-section-alt ema-reveal">
        <div className="ema-container">
          <div className="ema-section-head">
            <div>
              <p className="ema-kicker">Pédagogie Pratique</p>
              <h2 className="ema-h2">Procès fictifs & simulations d'audience</h2>
              <p className="ema-lead">
                Des exercices pédagogiques majeurs qui confrontent les élèves magistrats à des situations
                proches de la réalité juridictionnelle pour renforcer leurs compétences professionnelles.
              </p>
            </div>
            <Link className="ema-btn ema-btn-primary" to="/proces-fictifs">
              Découvrir l'activité →
            </Link>
          </div>

          <div className="ema-grid-3" style={{ marginTop: 20 }}>
            <div className="ema-card">
              <div className="ema-card-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3 className="ema-h3">Conduite d'audience</h3>
              <p className="ema-paragraph">
                Pratique de la direction des débats, gestion du temps et maîtrise des règles procédurales.
              </p>
            </div>

            <div className="ema-card">
              <div className="ema-card-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <path d="M12 19v3"/>
                  <path d="M8 22h8"/>
                </svg>
              </div>
              <h3 className="ema-h3">Prise de parole judiciaire</h3>
              <p className="ema-paragraph">
                Développement de l'expression orale, articulation des arguments et posture professionnelle.
              </p>
            </div>

            <div className="ema-card">
              <div className="ema-card-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <path d="M14 2v6h6"/>
                  <path d="M16 13H8"/>
                  <path d="M16 17H8"/>
                  <path d="M10 9H8"/>
                </svg>
              </div>
              <h3 className="ema-h3">Analyse des dossiers</h3>
              <p className="ema-paragraph">
                Étude critique des pièces, qualification juridique et construction de la décision.
              </p>
            </div>

            <div className="ema-card">
              <div className="ema-card-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </div>
              <h3 className="ema-h3">Rédaction judiciaire</h3>
              <p className="ema-paragraph">
                Rédaction de jugements, réquisitoires et décisions avec rigueur et clarté.
              </p>
            </div>

            <div className="ema-card">
              <div className="ema-card-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h3 className="ema-h3">Rôles du tribunal</h3>
              <p className="ema-paragraph">
                Exercice des fonctions de juge, parquet, défense et greffe dans des cas réels.
              </p>
            </div>

            <div className="ema-card">
              <div className="ema-card-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <h3 className="ema-h3">Éthique judiciaire</h3>
              <p className="ema-paragraph">
                Application concrète des principes déontologiques et sens de l'éthique.
              </p>
            </div>

          </div>

          {/* Statistiques des procès fictifs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '40px' }}>
            {[
              { value: '12', label: 'Procès organisés', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5"><path d="M12 3L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/></svg> },
              { value: '48', label: 'Participants', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
              { value: '8', label: 'Magistrats encadreurs', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
              { value: '120h', label: 'Heures de simulation', icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> }
            ].map((s, i) => (
              <div key={i} style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px',
                padding: '24px', textAlign: 'center', transition: 'all 0.3s',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
              }}
                onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(11,44,95,0.08)'; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.03)'; }}
              >
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>{s.icon}</div>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#0B2C5F' }}>{s.value}</div>
                <div style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500, marginTop: '4px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Link className="ema-btn ema-btn-primary" to="/proces-fictifs">
              Découvrir les procès fictifs
            </Link>
          </div>
        </div>
      </section>

      {/* VIE DE L'ÉCOLE - Section immersive */}
      <section className="ema-section ema-reveal" style={{ padding: '80px 0' }}>
        <div className="ema-container">
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="ema-kicker">Vie de l'École</span>
            <h2 className="ema-h2" style={{ marginTop: '8px' }}>Une communauté active et dynamique</h2>
            <p className="ema-lead" style={{ maxWidth: '640px', margin: '12px auto 0' }}>
              Découvrez la vie quotidienne de l'École de la Magistrature à travers ses activités pédagogiques, cérémonies officielles et événements institutionnels.
            </p>
          </div>

          {/* Grille immersive - 2 grandes + 4 petites */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(2, 260px)', gap: '16px' }}>
            {/* Grande carte 1 - span 2 colonnes */}
            <VieCard
              image="/media/photos/proces4.jpeg"
              badge="Pédagogie"
              title="Ateliers pratiques & simulations"
              desc="Exercices de mise en situation, simulations d'audiences et travaux de groupe."
              span="col2"
            />
            {/* Carte 2 */}
            <VieCard
              image="/media/photos/promotion8_5.jpg"
              badge="Cérémonies"
              title="Reprise des cours"
              desc="Moments forts."
            />
            {/* Carte 3 */}
            <VieCard
              image="/media/photos/proces4.jpeg"
              badge="Conférences"
              title="Conférences académiques"
              desc="Échanges avec des experts nationaux et internationaux."
            />
            {/* Grande carte 4 - span 2 colonnes */}
            <VieCard
              image="/media/photos/detente.jpeg"
              badge="Détente"
              title="Moment de détente de nos élèves magistrats"
              desc="Moment très agréable et enrichissant."
              span="col2"
            />
          </div>

          {/* Bande d'activités complémentaires */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '16px' }}>
            <VieCard
              image="/media/actualites/femmes-juges-2026/cover-maguy-fr.jpeg"
              badge="International"
              title="Coopération judiciaire"
              desc="Partenariats avec les grandes écoles de magistrature du monde."
              small
            />
            <VieCard
              image="/media/photos/promotion8_2.jpg"
              badge="Formation"
              title="Formations continues"
              desc="Perfectionnement des magistrats et greffiers en exercice."
              small
            />
            <div style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #0B2C5F 0%, #1e3a6e 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '32px',
              textAlign: 'center'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
              <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: 700, marginTop: '16px' }}>Explorez notre galerie</h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginTop: '8px' }}>Photos, vidéos et moments forts de l'institution</p>
              <Link to="/mediatheque" style={{ marginTop: '20px', background: '#D4AF37', color: '#071b3b', padding: '10px 24px', borderRadius: '100px', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
                Voir la médiathèque
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PORTRAITS DE MAGISTRATS */}
      <section className="ema-section ema-reveal" style={{ padding: '80px 0' }}>
        <div className="ema-container">
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="ema-kicker">Nos Magistrats</span>
            <h2 className="ema-h2" style={{ marginTop: '8px' }}>Portraits d'excellence</h2>
            <p className="ema-lead" style={{ maxWidth: '600px', margin: '12px auto 0' }}>
              Les femmes et hommes qui font l'excellence de la magistrature.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            {[
              { name: 'Gabrielle D. Pierre', role: 'Instruction', initials: 'GP', quote: "L'instruction est l'art de la rigueur et de la recherche de la vérité.", bg: 'linear-gradient(135deg, #D4AF37, #f4d03f)', color: '#0B2C5F' },
              { name: 'Norah A. Jean-François', role: 'Justice des mineurs', initials: 'NJ', quote: "Protéger l'enfance, c'est protéger l'avenir de notre société.", bg: 'linear-gradient(135deg, #0B2C5F, #1e40af)', color: '#fff' },
              { name: 'Bernard Saint-Vil', role: 'Juridiction des Référés', initials: 'BS', quote: "La célérité de la justice est garante de la confiance des citoyens.", bg: 'linear-gradient(135deg, #D4AF37, #f4d03f)', color: '#0B2C5F' },
              { name: 'André Saint-Isert', role: 'Fonction Siège', initials: 'AS', quote: "Juger avec impartialité exige une formation solide et permanente.", bg: 'linear-gradient(135deg, #0B2C5F, #1e40af)', color: '#fff' },
              { name: 'Frantzi Philemon', role: 'Fonction Parquet', initials: 'FP', quote: "Le parquet est le gardien de l'ordre public et de l'intérêt général.", bg: 'linear-gradient(135deg, #D4AF37, #f4d03f)', color: '#0B2C5F' },
              { name: 'Carvès Jean', role: 'Résolution de conflits', initials: 'CJ', quote: "La médiation est une voie de justice plus humaine et plus durable.", bg: 'linear-gradient(135deg, #0B2C5F, #1e40af)', color: '#fff' }
            ].map((p, i) => (
              <div key={i} style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: '20px',
                overflow: 'hidden', transition: 'all 0.3s', cursor: 'pointer'
              }}
                onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(11,44,95,0.1)'; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ height: '140px', background: p.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(255,255,255,0.25)', border: '2px solid rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 900, color: p.color }}>
                    {p.initials}
                  </div>
                </div>
                <div style={{ padding: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0B2C5F', margin: '0 0 4px' }}>{p.name}</h3>
                  <p style={{ fontSize: '13px', color: '#6B7280', fontWeight: 600, margin: 0 }}>{p.role}</p>
                  <div style={{ marginTop: '14px', padding: '12px', background: 'rgba(11,44,95,0.03)', borderRadius: '10px', borderLeft: '3px solid #D4AF37' }}>
                    <p style={{ fontSize: '13px', fontStyle: 'italic', color: '#374151', margin: 0, lineHeight: 1.5 }}>"{p.quote}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES - Carrousel */}
      <section className="ema-section ema-section-alt ema-reveal" style={{ padding: '80px 0' }}>
        <div className="ema-container">
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="ema-kicker">Témoignages</span>
            <h2 className="ema-h2" style={{ marginTop: '8px' }}>Ils racontent l'EMA</h2>
            <p className="ema-lead" style={{ maxWidth: '560px', margin: '12px auto 0' }}>
              Magistrats, greffiers, formateurs et anciens élèves partagent leur expérience de formation.
            </p>
          </div>

          <TestimonialsCarousel />

          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Link className="ema-btn ema-btn-secondary" to="/ecole/temoignages">
              Voir tous les témoignages
            </Link>
          </div>
        </div>
      </section>

      {/* PARTENAIRES */}
      <section className="ema-section">
        <div className="ema-container">
          <div className="ema-section-head">
            <div>
              <h2 className="ema-h2">Partenaires institutionnels</h2>
              <p className="ema-lead">
                L’École de la Magistrature développe ses activités en collaboration
                avec des institutions nationales et des partenaires internationaux.
              </p>
            </div>
          </div>

          <div className="ema-partners">
            <Link to="#" className="ema-partner" aria-label="Partenaire 1">
              <img src="/partenaires/iawj.jpg" alt="Partenaire 1" />
            </Link>

            <Link to="#" className="ema-partner" aria-label="Partenaire 2">
              <img src="/partenaires/onudc.png" alt="Partenaire 2" />
            </Link>

            <Link to="#" className="ema-partner" aria-label="Partenaire 3">
              <img src="/partenaires/renes.jpeg" alt="Partenaire 3" />
            </Link>

            <Link to="#" className="ema-partner" aria-label="Partenaire 4">
              <img src="/partenaires/pnud.png" alt="Partenaire 4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="ema-cta">
        <div className="ema-container ema-cta-box">
          <div>
            <h2 className="ema-h2">Prêt à déposer votre candidature ?</h2>
            <p className="ema-lead">
              Créez votre compte, téléversez vos pièces et suivez l’évolution de votre dossier.
            </p>
          </div>
          <div className="ema-cta-actions">
            <Link className="ema-btn ema-btn-primary" to="/register">Créer un compte</Link>
            <Link className="ema-btn ema-btn-secondary" to="/login">Se connecter</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

/* ===========================
   Components (helpers)
=========================== */

function PersonCard({ role, name, title, summary, photo, to, photoPosition = "center center" }) {
  return (
    <div className="ema-card ema-person">
      <div className="ema-person-top">
        <img
          className="ema-person-photo"
          src={photo}
          alt={`${role} - ${name}`}
          style={{ objectPosition: photoPosition }}
          onError={(e) => (e.currentTarget.src = "/gouvernance/placeholder.jpg")}
        />
        <span className="ema-person-role">{role}</span>
      </div>

      <div className="ema-person-body">
        <div className="ema-person-name">{name}</div>
        <div className="ema-muted">{title}</div>
        {summary && (
          <div className="ema-muted" style={{ marginTop: 8 }}>
            {summary}
          </div>
        )}

        <div className="ema-person-actions">
          <Link className="ema-btn ema-btn-secondary" to={to}>
            Voir profil
          </Link>
          <a className="ema-btn ema-btn-ghost" href="mailto:contact@ema.edu.ht">
            Contacter
          </a>
        </div>
      </div>
    </div>
  );
}

function QuickCard({ title, text, to, cta }) {
  return (
    <div className="ema-card">
      <h3 className="ema-card-title">{title}</h3>
      <p className="ema-card-text">{text}</p>
      <Link className="ema-link" to={to}>
        {cta} →
      </Link>
    </div>
  );
}

function ProgramCard({ title, text, to }) {
  return (
    <div className="ema-card">
      <h3 className="ema-card-title">{title}</h3>
      <p className="ema-card-text">{text}</p>
      <Link className="ema-link" to={to}>
        En savoir plus →
      </Link>
    </div>
  );
}

function TimelineStep({ n, title, text, to, cta }) {
  return (
    <div className="ema-step">
      <div className="ema-step-num">{n}</div>
      <div>
        <h3 className="ema-h3">{title}</h3>
        <p className="ema-paragraph">{text}</p>
        <Link className="ema-link" to={to}>
          {cta} →
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="ema-stat">
      <div className="ema-stat-value">{value}</div>
      <div className="ema-stat-label">{label}</div>
    </div>
  );
}

function VieCard({ image, badge, title, desc, span, small }) {
  return (
    <div style={{
      position: 'relative',
      borderRadius: '20px',
      overflow: 'hidden',
      gridColumn: span === 'col2' ? 'span 2' : 'span 1',
      minHeight: small ? '200px' : undefined,
      cursor: 'pointer'
    }}
      onMouseOver={(e) => {
        const img = e.currentTarget.querySelector('img');
        if (img) img.style.transform = 'scale(1.05)';
        e.currentTarget.querySelector('.vie-overlay').style.opacity = '1';
      }}
      onMouseOut={(e) => {
        const img = e.currentTarget.querySelector('img');
        if (img) img.style.transform = 'scale(1)';
        e.currentTarget.querySelector('.vie-overlay').style.opacity = '0.85';
      }}
    >
      <img
        src={image}
        alt={title}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }}
      />
      <div className="vie-overlay" style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.15) 60%)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        padding: '24px', opacity: 0.85, transition: 'opacity 0.3s ease'
      }}>
        <span style={{ background: 'rgba(212,175,55,0.9)', color: '#071b3b', padding: '4px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 700, alignSelf: 'flex-start', marginBottom: '10px' }}>{badge}</span>
        <h3 style={{ color: '#fff', fontSize: small ? '16px' : '18px', fontWeight: 700, margin: 0, lineHeight: 1.3 }}>{title}</h3>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', margin: '6px 0 0', lineHeight: 1.4 }}>{desc}</p>
      </div>
    </div>
  );
}

function TestimonialsCarousel() {
  const [active, setActive] = React.useState(0);

  const testimonials = [
    {
      name: "Juge Jean Dupont",
      title: "Tribunal de première instance de Port-au-Prince",
      category: "Magistrat",
      quote: "La formation à l'EMA m'a donné les outils nécessaires pour exercer ma fonction avec rigueur et intégrité. Chaque jour, je m'efforce de rendre justice avec équité et professionnalisme."
    },
    {
      name: "Juge Marie Pierre",
      title: "Cour d'appel de Cap-Haïtien",
      category: "Magistrate",
      quote: "L'EMA m'a préparée aux défis de la magistrature. Les stages pratiques et les enseignements théoriques m'ont permis de développer les compétences essentielles à mon rôle de juge."
    },
    {
      name: "Greffier Jean-Baptiste",
      title: "Greffe du Tribunal de Port-au-Prince",
      category: "Greffier",
      quote: "Les formations continues de l'EMA sont indispensables pour notre profession. Elles nous permettent de nous perfectionner et d'offrir un service de qualité aux justiciables."
    },
    {
      name: "Formateur Michel Célestine",
      title: "Professeur de droit civil",
      category: "Formateur",
      quote: "Former les futurs magistrats est une mission noble. Voir nos élèves réussir et servir la justice avec intégrité est notre plus grande récompense."
    },
    {
      name: "Juge Rose-Anne Michel",
      title: "Tribunal pour enfants de Port-au-Prince",
      category: "Magistrate",
      quote: "La formation continue de l'EMA me permet de rester à jour avec les évolutions juridiques et d'améliorer constamment ma pratique judiciaire au service des enfants."
    }
  ];

  React.useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const t = testimonials[active];

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        background: '#fff', borderRadius: '24px', padding: '48px',
        boxShadow: '0 8px 32px rgba(11,44,95,0.06)', border: '1px solid #e5e7eb',
        textAlign: 'center', maxWidth: '720px', margin: '0 auto',
        minHeight: '240px', display: 'flex', flexDirection: 'column', justifyContent: 'center'
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="#D4AF37" style={{ margin: '0 auto 16px', opacity: 0.6 }}>
          <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z"/>
          <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
        </svg>
        <p style={{ fontSize: '17px', color: '#374151', lineHeight: 1.7, fontStyle: 'italic', margin: '0 0 24px' }}>
          {t.quote}
        </p>
        <div>
          <div style={{ fontWeight: 700, color: '#0B2C5F', fontSize: '15px' }}>{t.name}</div>
          <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>{t.title}</div>
          <span style={{ display: 'inline-block', marginTop: '8px', background: 'rgba(212,175,55,0.1)', color: '#D4AF37', padding: '3px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 700 }}>
            {t.category}
          </span>
        </div>
      </div>

      {/* Navigation dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
        {testimonials.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            style={{
              width: active === i ? '24px' : '8px',
              height: '8px',
              borderRadius: '100px',
              background: active === i ? '#0B2C5F' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            aria-label={`Témoignage ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}